import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import BetaPagination from 'components/upb_viewer_beta/BetaPagination';
import { constructUrlPathSegment } from 'components/upb_viewer/UPBViewer';
import { DEFAULT_UPB_PAGE_SIZE } from 'lib/constants';
import MsvCardGrid from 'components/upb_mfr_viewer/MsvCardGrid';
import useMfrBrands from 'components/upb_mfr_viewer/hooks/useMfrBrands';
import {
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_WEIGHT_MEDIUM,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * MfrBrandsPage — MSV manufacturer-detail grid: the manufacturer's brands
 * ordered by GMV, scoped by the same category/distributor/search filters.
 * Clicking a brand drills into its products.
 */
function MfrBrandsPage({
  manufacturerId,
  manufacturerSeg,
  searchQuery,
  l1CategoryId,
  l2CategoryId,
  distributorId,
  thirdPartyTag,
  onNavigate,
  baseURL,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const page = useMemo(() => Math.max(currentPage - 1, 0), [currentPage]);
  const offset = useMemo(() => page * DEFAULT_UPB_PAGE_SIZE, [page]);

  useEffect(() => {
    setCurrentPage(1);
  }, [manufacturerId, searchQuery, l1CategoryId, l2CategoryId, distributorId, thirdPartyTag]);

  const { data: { brands = [], count = 0 } = {}, loading } = useMfrBrands({
    manufacturerId,
    offset,
    limit: DEFAULT_UPB_PAGE_SIZE,
    searchQuery,
    l1CategoryId,
    l2CategoryId,
    distributorId,
    thirdPartyTag,
  });

  const totalPages = Math.max(Math.ceil(count / DEFAULT_UPB_PAGE_SIZE), 1);

  const handleClick = brand => {
    const brandSeg = constructUrlPathSegment({ value: brand.id, label: brand.name });
    onNavigate(`/${baseURL}/${manufacturerSeg}/${brandSeg}`);
  };

  return (
    <div className={css(styles.page)}>
      {/* Breadcrumb + title + distributor filter live in the container's
          header card (mock); this row only carries the count. */}
      <div className={css(styles.head)}>
        <span className={css(styles.count)}>
          {count === 0 ? 'No brands' : `${count.toLocaleString('en-US')} brands`}
        </span>
      </div>

      <MsvCardGrid
        items={brands}
        loading={loading}
        onItemClick={handleClick}
        emptyLabel="No brands found."
      />

      {totalPages > 1 && (
        <BetaPagination
          totalPages={totalPages}
          currentPageNumber={page}
          onPageChange={newPage => setCurrentPage(newPage + 1)}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  },

  head: {
    display: 'flex',
    justifyContent: 'flex-end',
  },

  count: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_3,
  },
});

MfrBrandsPage.propTypes = {
  manufacturerId: PropTypes.number,
  manufacturerSeg: PropTypes.string.isRequired,
  searchQuery: PropTypes.string,
  l1CategoryId: PropTypes.number,
  l2CategoryId: PropTypes.number,
  distributorId: PropTypes.number,
  thirdPartyTag: PropTypes.string,
  onNavigate: PropTypes.func.isRequired,
  baseURL: PropTypes.string.isRequired,
};

MfrBrandsPage.defaultProps = {
  manufacturerId: null,
  searchQuery: '',
  l1CategoryId: null,
  l2CategoryId: null,
  distributorId: null,
  thirdPartyTag: null,
};

export default MfrBrandsPage;
